/**
 * Sanitize a free-text organisation name into a Keycloak-safe organisation
 * `alias`.
 *
 * Keycloak uses the organisation alias as a unique, URL-safe identifier, so it
 * must not contain whitespace or dots. To be safe across Keycloak versions we
 * reduce the alias to a lowercase slug of ASCII letters, digits and dashes:
 *
 *  - German umlauts are transliterated the German way (ä → ae, ö → oe,
 *    ü → ue, ß → ss) instead of just dropping the diaeresis;
 *  - any remaining diacritics are folded to their base ASCII letter
 *    (é → e, ç → c …);
 *  - any run of characters that is not `a-z` / `0-9` (whitespace, `.`, `@`,
 *    `/`, `&`, quotes, brackets, …) collapses to a single dash;
 *  - leading/trailing dashes are trimmed so the alias never starts or ends with
 *    a separator.
 */
const GERMAN_TRANSLITERATIONS: Array<[RegExp, string]> = [
  [/ä/g, "ae"],
  [/ö/g, "oe"],
  [/ü/g, "ue"],
  [/ß/g, "ss"],
];

export const sanitizeOrganizationAlias = (value: string): string => {
  let result = value.toLowerCase();
  // German umlauts must become ae/oe/ue (not a/o/u) before we strip diacritics
  for (const [pattern, replacement] of GERMAN_TRANSLITERATIONS) {
    result = result.replace(pattern, replacement);
  }
  return result
    .normalize("NFKD")
    // strip the combining diacritical marks left behind by NFKD decomposition
    .replace(/[̀-ͯ]/g, "")
    // any run of non-alphanumeric characters becomes a single dash
    .replace(/[^a-z0-9]+/g, "-")
    // no leading/trailing dashes
    .replace(/^-+|-+$/g, "");
};
